/* <adg-preview-shell> — PLACEMENT layer for ADG previews. Product-agnostic:
 * it hosts any <adg-product-preview> (or future product) and owns WHERE it
 * lives — inline stays on the page; this shell does overlay + dock.
 *
 *   shell.open(sourceEl)   full-screen overlay (mobile: sheet, desktop: capped
 *                          modal), FLIP-zoomed from the source's screen rect
 *   shell.viewImage(opts)  same overlay, showing a single artwork image —
 *                          thumb shows INSTANTLY, full crossfades in when decoded
 *                          (no stale last-image, no blank, no loading counter)
 *   shell.close()          reverse animation, restore scroll
 *   shell.dock(sourceEl)   floating mini, fixed bottom-right; click -> open
 *   shell.undock()
 *
 * It also listens document-wide for `adg-open` (fired by mini / no-interact
 * instances) and opens from whichever element fired it.
 * Self-contained on purpose — in the Shopify theme it can later delegate
 * scroll-lock/backdrop to DialogComponent without changing its API.
 */
(() => {
  const MIRROR = ["product", "arts", "assets-base", "asset-map", "slots", "label", "quantity",
    "interval", "resume-delay", "pick-hold", "placeholder-label", "showcase", "data-byo"];
  const isPhone = () => matchMedia("(max-width: 640px), (pointer: coarse) and (max-width: 920px)").matches;

  class ADGPreviewShell extends HTMLElement {
    connectedCallback() {
      if (!this.shadowRoot) this.attachShadow({ mode: "open" });
      this.shadowRoot.innerHTML = `<style>
        :host { display:contents; }
        .veil { position:fixed; inset:0; background:rgba(24,20,14,.76);
          -webkit-backdrop-filter:blur(8px); backdrop-filter:blur(8px);
          opacity:0; transition:opacity .32s ease; z-index:9000; }
        .veil.in { opacity:1; }
        /* FLOATING popup: no card-around-the-card — the products ARE cards; they
           float straight on the veil with a slim title pill above */
        .panel { position:fixed; z-index:9001; background:transparent; display:flex;
          flex-direction:column; left:50%; top:50%; translate:-50% -50%;
          width:min(560px, 92vw); max-height:94vh;
          transform-origin:top left; will-change:transform; }
        .panel.sheet { left:0; top:auto; bottom:0; translate:0 0; width:100vw;
          max-height:none; height:100dvh; justify-content:center; transform-origin:center bottom; }
        .bar { display:flex; align-items:center; gap:12px; align-self:center;
          background:rgba(22,17,10,.55); border-radius:99px; padding:7px 8px 7px 20px;
          color:#f5f0e4; font:700 14px -apple-system,sans-serif; letter-spacing:.03em;
          backdrop-filter:blur(4px); margin-bottom:16px; }
        .grab { display:none; }
        .sheet .grab { display:block; width:46px; height:5px; border-radius:3px;
          background:rgba(245,240,228,.55); margin:0 auto 14px; }
        .bar .x, .bar .dk { border:0; background:rgba(245,240,228,.16); color:#f5f0e4;
          width:30px; height:30px; border-radius:50%; font:700 14px/1 -apple-system,sans-serif;
          cursor:pointer; display:inline-flex; align-items:center; justify-content:center; }
        .bar .x:hover, .bar .dk:hover { background:rgba(245,240,228,.3); }
        .bar .dk { display:none; }                 /* shown only for product popups (not the lightbox) */
        .bar .dk svg { width:15px; height:15px; }
        /* the ACTION color carries "where you are" — shell chrome only */
        .bar .pos { background:var(--adg-accent, #ff4a06); color:#fff; border-radius:99px;
          padding:.32em .85em; font:700 11.5px -apple-system,sans-serif; letter-spacing:.07em;
          white-space:nowrap; }
        .bar .pos:empty { display:none; }
        .dpos { position:absolute; left:50%; bottom:-9px; translate:-50% 0;
          background:var(--adg-accent, #ff4a06); color:#fff; border-radius:99px;
          padding:.3em .7em; font:700 10px -apple-system,sans-serif; letter-spacing:.06em;
          white-space:nowrap; pointer-events:none; }
        .dpos:empty { display:none; }
        .body { padding:0 8px 10px; overflow:visible; display:flex; flex-direction:column; align-items:center; }
        /* floating = no panel edge to violate; size so the WORST-CASE pose stays
           on-screen: postcard back rotates 90° (×1.34), notecard opens (×1.95),
           calendar is 2.6× tall (cap by viewport height) */
        .body adg-product-preview { width:min(78%, calc((94dvh - 130px) * .75)); }
        .body adg-product-preview[product="notecard"] { width:min(64%, 46vw); }
        .body adg-product-preview[product="calendar"] { width:min(60%, calc((94dvh - 150px) * .3846)); }
        .sheet .body adg-product-preview { width:min(74%, calc((100dvh - 150px) * .75)); }
        .sheet .body adg-product-preview[product="notecard"] { width:48%; }
        .sheet .body adg-product-preview[product="calendar"] { width:min(60%, calc((100dvh - 170px) * .3846)); }
        /* dock is BOXLESS too — the piece floats bottom-right, its own shadow is the chrome */
        /* bottom was a hardcoded 92px — the submit bar's 70 plus a 22 gap,
           measured by hand. Now it rides layer 1 of the shared stack, so it
           follows the submit bar wherever a cookie banner pushes it. */
        .dock { position:fixed; right:26px; bottom:var(--adg-inset-1, 92px); z-index:8990; width:118px;
          cursor:pointer; transition:transform .2s; filter:drop-shadow(0 10px 22px rgba(20,15,8,.45)); }
        .dock:hover { transform:translateY(-3px) scale(1.04); }
        .dock .dx { position:absolute; top:-7px; right:-7px; width:22px; height:22px;
          border-radius:50%; border:0; background:#3a3125; color:#f5f0e4;
          font:700 11px/1 -apple-system,sans-serif; cursor:pointer; }
        /* ---- image viewer: thumb-first, crossfade to full (no stale/blank/counter) ---- */
        .imgview { display:flex; flex-direction:column; align-items:center; gap:12px; }
        .imgview__frame { position:relative; border-radius:5px; overflow:hidden;
          background:#efe9d8; line-height:0; box-shadow:0 24px 70px rgba(20,15,8,.55); }
        .imgview__thumb { display:block; max-width:88vw; max-height:80vh; width:auto; height:auto;
          filter:blur(7px); transform:scale(1.04); }         /* soft while the sharp one loads */
        .imgview__full { position:absolute; inset:0; width:100%; height:100%; object-fit:cover;
          opacity:0; transition:opacity .4s ease; }
        .imgview.ready .imgview__full { opacity:1; }
        .imgview.nothumb .imgview__frame { min-width:140px; min-height:140px;
          display:flex; align-items:center; justify-content:center; }
        .imgview.nothumb .imgview__full { position:static; width:auto; height:auto;
          max-width:88vw; max-height:80vh; display:block; }
        .imgview.nothumb:not(.ready) .imgview__frame::after { content:""; width:26px; height:26px;
          border:3px solid rgba(93,70,50,.25); border-top-color:#5d4632; border-radius:50%;
          animation:adg-spin .8s linear infinite; }
        @keyframes adg-spin { to { transform:rotate(360deg); } }
        .imgview__cap { color:#f5f0e4; font:italic 14px Georgia,serif; text-align:center; max-width:80vw; }
        .sheet .imgview__thumb, .sheet .imgview.nothumb .imgview__full { max-height:72vh; }
      </style>`;
      if (!this.__wired) {
        this.__wired = true;
        // declarative auto-dock: any preview flagged `auto-dock` docks itself when
        // scrolled out of view (desktop), and comes home when scrolled back
        requestAnimationFrame(() =>
          document.querySelectorAll("adg-product-preview[auto-dock]").forEach((el) => this.watch(el)));
        document.addEventListener("adg-open", (e) => {
          // shadow retargeting makes e.target = this shell for events born inside it —
          // composedPath()[0] is the TRUE origin (e.g. the docked mini)
          const origin = e.composedPath()[0];
          if (this._dockEl && this._dockEl.contains(origin)) this.open(this._source, this._dockEl);
          else if (origin !== this && !this.shadowRoot.contains(origin)) this.open(origin);
        });
        this._esc = (e) => { if (e.key === "Escape" && this._panel) this.close(); };
      }
    }

    _mirror(src, el, forPopup) {
      MIRROR.forEach((a) => {
        const v = src?.getAttribute?.(a);
        if (v != null) el.setAttribute(a, v); else el.removeAttribute(a);
      });
      const pos = this._seedPos();          // EVERY placement inherits the ONE shared position
      if (pos != null) el.setAttribute("focus", pos);
      if (forPopup) el.setAttribute("auto", "");
    }
    _posOf(el) {   // product-agnostic "where are you" (calendar counts months, cards count cards)
      if (!el) return null;
      if (el.getAttribute?.("product") === "calendar") return el.idx != null ? Math.max(0, el.idx - 1) : null;
      return el.cardIdx ?? null;
    }
    // ONE shared browse position across embed / dock / popup. Each placement seeds
    // from it on appear; the active one keeps it current; transitions write it back.
    _seedPos() { return this._pos != null ? this._pos : this._posOf(this._source); }
    _dockMini() { return this._dockEl?.querySelector("adg-product-preview") || null; }
    _driver() {                                    // who runs the animation clock
      if (this._panel) return this._inner;         // the popup, while open
      const dm = this._dockMini();
      if (dm) return dm;                           // else the dock animates ITSELF (source may be dormant/static)
      return this._source;
    }
    // ONE clock drives; every other visible placement MIRRORS it live, so they flip
    // the same card at the same instant instead of on independent timers.
    _wirePos(el) {
      if (!el || el.__posWired) return;
      el.__posWired = true;
      el.addEventListener("adg-position", (e) => {
        const i = e.detail?.index;
        if (i == null) return;
        this._pos = i;
        if (el === this._driver())
          [this._source, this._dockMini(), this._inner].forEach((m) => {
            if (m && m !== el && m._follow) m.syncTo(i);
          });
      });
    }
    _resync() {   // pick the driver, pause the rest, mark them as followers
      const driver = this._driver();
      [this._source, this._dockMini(), this._inner].forEach((el) => {
        if (!el) return;
        el._follow = el !== driver;
        if (el === driver) el.play?.(); else el.pause?.();
      });
    }
    _snapTo(el, pos) {   // jump an instance to pos WITHOUT a visible flight (used off-screen)
      if (!el?.show || this._posOf(el) === pos) return;
      el.show(pos); el.finishFlight?.(); el.finishNc?.(); el.finishCal?.();
    }
    // LIVE mirror: a pick made while the popup/dock is showing must reach the twin
    // (which then loads the new image + runs its own pick-hold countdown). Only DATA
    // attributes propagate — presentation ones (mini/focus/label) stay twin-owned.
    _syncTwin(src, twin) {
      const DATA = ["slots", "arts", "quantity", "assets-base"];
      const push = () => DATA.forEach((a) => {
        const v = src.getAttribute(a);
        if (v != null && twin.getAttribute(a) !== v) twin.setAttribute(a, v);
        else if (v == null && twin.hasAttribute(a)) twin.removeAttribute(a);
      });
      const mo = new MutationObserver(push);
      mo.observe(src, { attributes: true, attributeFilter: DATA });
      return mo;
    }

    // shared frame: builds veil + floating panel + bar, runs the FLIP entrance,
    // then lets `fill(body, panel)` populate the content. `exitTo` is the element
    // the close animation zooms back toward.
    _present(fill, { title = "", zoomFrom = null, exitTo = null } = {}) {
      if (this._panel) this.close(true);            // replace instantly, no double overlays
      this._lockScroll();
      this._exitTo = exitTo;
      const veil = document.createElement("div"); veil.className = "veil";
      const panel = document.createElement("div"); panel.className = "panel" + (isPhone() ? " sheet" : "");
      panel.innerHTML = `<div class="grab"></div><div class="bar"><span class="title"></span><span class="pos"></span>` +
        `<button class="dk" aria-label="Dock to corner" title="Dock to corner"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M13 20v-6h6"/></svg></button>` +
        `<button class="x" aria-label="Close">✕</button></div><div class="body"></div>`;
      panel.querySelector(".title").textContent = title;
      this.shadowRoot.append(veil, panel);
      this._panel = panel; this._veil = veil;
      fill(panel.querySelector(".body"), panel);
      panel.querySelector(".x").addEventListener("click", () => this.close());
      veil.addEventListener("click", () => this.close());
      addEventListener("keydown", this._esc);

      // entrance: sheet slides up on phones; FLIP zoom from the origin rect on desktop
      if (panel.classList.contains("sheet")) {
        panel.style.transform = "translateY(100%)";
        this._sheetDrag(panel);
      } else {
        const F = panel.getBoundingClientRect();
        const S = zoomFrom?.getBoundingClientRect?.();
        if (S && S.width > 4) panel.style.transform =
          `translate(${S.left - F.left}px, ${S.top - F.top}px) scale(${S.width / F.width}, ${S.height / F.height})`;
        else panel.style.transform = "translate(0, 24px) scale(.96)";
        panel.style.opacity = "0.4";
      }
      void panel.offsetWidth;
      panel.style.transition = "transform .36s cubic-bezier(.3,.1,.25,1), opacity .3s";
      panel.style.transform = "none"; panel.style.opacity = "1";
      veil.classList.add("in");
      if (this._dockEl) this._dockEl.style.display = "none";   // popup supersedes the dock
    }

    open(source, fromEl = null) {
      this._source = source && source.getBoundingClientRect ? source : this._source;
      const src = this._source;
      if (!src) return;
      this._present((body, panel) => {
        const inner = document.createElement("adg-product-preview");
        const pos = panel.querySelector(".pos");
        inner.addEventListener("adg-position", (e) => { pos.textContent = e.detail.text; });
        this._mirror(src, inner, true);
        panel.querySelector(".title").textContent = src.getAttribute?.("label") || "";
        inner.removeAttribute("label");
        body.appendChild(inner);
        this._inner = inner;
        this._twinMO?.disconnect();
        this._twinMO = this._syncTwin(src, inner);      // picks keep flowing into the popup
        this._wirePos(src); this._wirePos(inner);
        const dk = panel.querySelector(".dk");          // product popups can dock straight from here
        dk.style.display = "inline-flex";
        dk.addEventListener("click", () => { this.dock(src); this.close(); });
      }, { zoomFrom: fromEl || src, exitTo: src });
      this._resync();                                   // popup becomes the driver; embed + dock follow
    }

    // GENERAL-PURPOSE artwork viewer — usable anywhere, not just for products.
    //   shell.viewImage({ src, thumb, title, caption }, clickedThumbEl)
    viewImage(opts = {}, fromEl = null) {
      const { src, thumb, title = "", caption = "" } = opts;
      if (!src && !thumb) return;
      this._source = null;                          // images don't sync a position back
      this._present((body) => {
        const view = document.createElement("div");
        view.className = "imgview" + (thumb ? "" : " nothumb");
        view.innerHTML =
          `<div class="imgview__frame">` +
            (thumb ? `<img class="imgview__thumb" src="${thumb}" alt="">` : ``) +
            `<img class="imgview__full" alt="${title.replace(/"/g, "&quot;")}">` +
          `</div>` +
          (caption ? `<div class="imgview__cap">${caption}</div>` : ``);
        const full = view.querySelector(".imgview__full");
        // reveal ONLY once decoded — never a blank or half-painted frame. A fresh
        // node every time means there is no previous image to flash.
        const reveal = () => view.classList.add("ready");
        full.addEventListener("load", reveal);
        full.addEventListener("error", reveal);     // a bad URL must not hang the veil
        full.decoding = "async";
        full.src = src || thumb;
        if (full.complete && full.naturalWidth) reveal();
        body.appendChild(view);
        this._inner = view;
      }, { title, zoomFrom: fromEl, exitTo: fromEl });
    }

    close(instant = false) {
      const { _panel: panel, _veil: veil, _inner: inner } = this;
      if (!panel) return;
      // state continuity: the popup's final position becomes the shared position,
      // and BOTH the page source and the re-appearing dock snap to it
      const pos = this._posOf(inner);
      if (pos != null) {
        this._pos = pos;
        this._snapTo(this._source, pos);
        this._snapTo(this._dockEl?.querySelector("adg-product-preview"), pos);
      }
      this._panel = this._veil = this._inner = null;
      this._twinMO?.disconnect(); this._twinMO = null;
      removeEventListener("keydown", this._esc);
      const done = () => { panel.remove(); veil.remove(); };
      if (instant) done();
      else {
        veil.classList.remove("in");
        panel.style.transition = "transform .3s cubic-bezier(.4,.05,.6,1), opacity .28s";
        if (panel.classList.contains("sheet")) panel.style.transform = "translateY(100%)";
        else {
          const tgt = this._exitTo, F = panel.getBoundingClientRect();
          const S = tgt?.isConnected ? tgt.getBoundingClientRect() : null;
          panel.style.transform = S && S.width > 4
            ? `translate(${S.left - F.left}px, ${S.top - F.top}px) scale(${S.width / F.width}, ${S.height / F.height})`
            : "translate(0, 24px) scale(.96)";
          panel.style.opacity = "0";
        }
        setTimeout(done, 320);
      }
      this._unlockScroll();
      if (this._dockEl) this._dockEl.style.display = "";
      this._resync();                        // driver reverts to the embed; dock mirrors it again
    }

    dock(source) {
      this._source = source && source.getBoundingClientRect ? source : this._source;
      if (!this._source || this._dockEl) return;
      const d = document.createElement("div"); d.className = "dock";
      const mini = document.createElement("adg-product-preview");
      const dpos = document.createElement("div"); dpos.className = "dpos";
      mini.addEventListener("adg-position", (e) => {   // compact form on the dock badge
        dpos.textContent = (e.detail.text || "").replace(" OF ", "/");
      });
      this._mirror(this._source, mini, false);
      mini.removeAttribute("label");
      mini.setAttribute("mini", "");
      d.appendChild(mini);
      d.appendChild(dpos);
      const x = document.createElement("button"); x.className = "dx"; x.textContent = "✕";
      x.setAttribute("aria-label", "Dismiss preview");
      x.addEventListener("click", (e) => {
        e.stopPropagation();
        this._snoozed = true;         // user dismissed: no re-dock until it re-enters view once
        this.undock();
      });
      d.appendChild(x);
      this.shadowRoot.appendChild(d);
      this._dockEl = d;              // mini's own click fires adg-open -> open()
      this._dockMO?.disconnect();
      this._dockMO = this._syncTwin(this._source, mini);   // picks keep flowing into the dock
      this._wirePos(this._source); this._wirePos(mini);
      this._resync();                                      // embed drives; dock mirrors it in lockstep
      this._reportHeight();
    }
    undock() { this._dockEl?.remove(); this._dockEl = null; this._autoDocked = false;
      this._dockMO?.disconnect(); this._dockMO = null;
      this._source && this._source.play?.();   // embed resumes as sole driver
      window.adgSetDockHeight?.(0);
    }

    /* The dock lives in this shadow root, so adg-bottom-inset.js cannot
       measure it from the document. Report our own height instead, and let
       it publish --adg-inset-2 for whatever stacks above us (the to-top
       pill). Deferred a frame because the mini sizes itself from its art. */
    _reportHeight() {
      const send = () => {
        const h = this._dockEl?.getBoundingClientRect().height || 0;
        window.adgSetDockHeight?.(h);
      };
      requestAnimationFrame(send);
      if (window.ResizeObserver && this._dockEl) {
        this._dockRO?.disconnect();
        this._dockRO = new ResizeObserver(send);
        this._dockRO.observe(this._dockEl);
      }
    }

    watch(source) {   // auto-dock lifecycle for one embedded instance
      this._watched = source;
      this._io?.disconnect();
      this._io = new IntersectionObserver(([en]) => {
        if (isPhone()) return;                                   // desktop-only behavior
        if (en.isIntersecting) {
          this._snoozed = false;                                 // back in view: forgiven
          if (this._autoDocked) {
            this._snapTo(source, this._pos);                     // embed catches up to the dock's position
            this.undock();                                       // then the dock goes home
          }
        } else if (!this._panel && !this._snoozed && !this._dockEl) {
          this.dock(source);
          this._autoDocked = true;
        }
      }, { threshold: 0.05 });
      this._io.observe(source);
    }

    _sheetDrag(panel) {              // swipe the grab-bar down to dismiss
      const grab = panel.querySelector(".grab, .bar");
      let sy = null;
      grab.addEventListener("pointerdown", (e) => { sy = e.clientY; grab.setPointerCapture(e.pointerId); });
      grab.addEventListener("pointermove", (e) => {
        if (sy == null) return;
        const dy = Math.max(0, e.clientY - sy);
        panel.style.transition = "none";
        panel.style.transform = `translateY(${dy}px)`;
      });
      const up = (e) => {
        if (sy == null) return;
        const dy = e.clientY - sy; sy = null;
        if (dy > 110) this.close();
        else { panel.style.transition = "transform .25s ease"; panel.style.transform = "none"; }
      };
      grab.addEventListener("pointerup", up);
      grab.addEventListener("pointercancel", up);
    }

    _lockScroll() {
      this._sy = scrollY;
      Object.assign(document.body.style, { position: "fixed", top: `-${this._sy}px`, left: "0", right: "0" });
    }
    _unlockScroll() {
      Object.assign(document.body.style, { position: "", top: "", left: "", right: "" });
      scrollTo(0, this._sy || 0);
    }
  }
  customElements.define("adg-preview-shell", ADGPreviewShell);
})();
